/**
 * The single time-zone normaliser for the web app (locale/time-zone audit,
 * 2026-09-26). Mirrors apps/api/src/common/time/zone.ts; the alias table is a
 * copy of the API's LEGACY_TIME_ZONE_ALIASES and a parity spec fails if they drift.
 *
 * Chrome reports India as the legacy 'Asia/Calcutta'. PostgreSQL 17 on the
 * prod and staging boxes rejects that name, and it was being stored raw from
 * logins, campaigns and announcements. Normalise before sending anything.
 */

/** ICU/CLDR canonical ids and other backward links -> current IANA names. */
export const LEGACY_TIME_ZONE_ALIASES: Readonly<Record<string, string>> = {
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Thimbu': 'Asia/Thimphu',
  'Asia/Dacca': 'Asia/Dhaka',
  'Asia/Macao': 'Asia/Macau',
  'Asia/Ulan_Bator': 'Asia/Ulaanbaatar',
  'Asia/Ujung_Pandang': 'Asia/Makassar',
  'Asia/Ashkhabad': 'Asia/Ashgabat',
  'Asia/Chongqing': 'Asia/Shanghai',
  'Asia/Chungking': 'Asia/Shanghai',
  'Asia/Harbin': 'Asia/Shanghai',
  'Asia/Kashgar': 'Asia/Urumqi',
  'Asia/Istanbul': 'Europe/Istanbul',
  'Asia/Tel_Aviv': 'Asia/Jerusalem',
  'Europe/Kiev': 'Europe/Kyiv',
  'Europe/Uzhgorod': 'Europe/Kyiv',
  'Europe/Zaporozhye': 'Europe/Kyiv',
  'Europe/Belfast': 'Europe/London',
  'Europe/Tiraspol': 'Europe/Chisinau',
  'Europe/Nicosia': 'Asia/Nicosia',
  'Africa/Asmera': 'Africa/Asmara',
  'Africa/Timbuktu': 'Africa/Bamako',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'America/Catamarca': 'America/Argentina/Catamarca',
  'America/Cordoba': 'America/Argentina/Cordoba',
  'America/Jujuy': 'America/Argentina/Jujuy',
  'America/Mendoza': 'America/Argentina/Mendoza',
  'America/Indianapolis': 'America/Indiana/Indianapolis',
  'America/Fort_Wayne': 'America/Indiana/Indianapolis',
  'America/Knox_IN': 'America/Indiana/Knox',
  'America/Louisville': 'America/Kentucky/Louisville',
  'America/Godthab': 'America/Nuuk',
  'America/Atka': 'America/Adak',
  'America/Ensenada': 'America/Tijuana',
  'America/Santa_Isabel': 'America/Tijuana',
  'America/Porto_Acre': 'America/Rio_Branco',
  'America/Shiprock': 'America/Denver',
  'America/Virgin': 'America/Puerto_Rico',
  'America/Montreal': 'America/Toronto',
  // ICU (Node 24, Chrome) reports America/Atikokan as this backward link.
  'America/Coral_Harbour': 'America/Atikokan',
  'Atlantic/Faeroe': 'Atlantic/Faroe',
  'Atlantic/Jan_Mayen': 'Europe/Berlin',
  'Pacific/Enderbury': 'Pacific/Kanton',
  'Pacific/Ponape': 'Pacific/Pohnpei',
  'Pacific/Truk': 'Pacific/Chuuk',
  'Pacific/Yap': 'Pacific/Chuuk',
  'Pacific/Johnston': 'Pacific/Honolulu',
  'Pacific/Samoa': 'Pacific/Pago_Pago',
  'Australia/ACT': 'Australia/Sydney',
  'Australia/Canberra': 'Australia/Sydney',
  'Australia/NSW': 'Australia/Sydney',
  'Australia/North': 'Australia/Darwin',
  'Australia/Queensland': 'Australia/Brisbane',
  'Australia/South': 'Australia/Adelaide',
  'Australia/Tasmania': 'Australia/Hobart',
  'Australia/Victoria': 'Australia/Melbourne',
  'Australia/West': 'Australia/Perth',
  'US/Alaska': 'America/Anchorage',
  'US/Aleutian': 'America/Adak',
  'US/Arizona': 'America/Phoenix',
  'US/Central': 'America/Chicago',
  'US/Eastern': 'America/New_York',
  'US/Hawaii': 'Pacific/Honolulu',
  'US/Mountain': 'America/Denver',
  'US/Pacific': 'America/Los_Angeles',
  'US/Samoa': 'Pacific/Pago_Pago',
  'Canada/Atlantic': 'America/Halifax',
  'Canada/Central': 'America/Winnipeg',
  'Canada/Eastern': 'America/Toronto',
  'Canada/Mountain': 'America/Edmonton',
  'Canada/Newfoundland': 'America/St_Johns',
  'Canada/Pacific': 'America/Vancouver',
  Japan: 'Asia/Tokyo',
  Singapore: 'Asia/Singapore',
  Hongkong: 'Asia/Hong_Kong',
  Israel: 'Asia/Jerusalem',
  Egypt: 'Africa/Cairo',
  Turkey: 'Europe/Istanbul',
  Iran: 'Asia/Tehran',
  Cuba: 'America/Havana',
  Jamaica: 'America/Jamaica',
  Poland: 'Europe/Warsaw',
  Portugal: 'Europe/Lisbon',
  Eire: 'Europe/Dublin',
  GB: 'Europe/London',
  NZ: 'Pacific/Auckland',
  PRC: 'Asia/Shanghai',
  ROC: 'Asia/Taipei',
  ROK: 'Asia/Seoul',
  UCT: 'UTC',
  Zulu: 'UTC',
  GMT: 'UTC',
  Greenwich: 'UTC',
  Universal: 'UTC',
  'Etc/UTC': 'UTC',
  'Etc/GMT': 'UTC',
}

/**
 * The current IANA id for `tz` (aliases mapped, casing repaired by ICU), or
 * null when the value is empty or not a real zone.
 */
export function normalizeTimeZone(tz: unknown): string | null {
  if (typeof tz !== 'string') return null
  const trimmed = tz.trim()
  if (!trimmed) return null
  const aliased = LEGACY_TIME_ZONE_ALIASES[trimmed] ?? trimmed
  let resolved: string
  try {
    resolved = new Intl.DateTimeFormat('en-US', { timeZone: aliased }).resolvedOptions().timeZone
  } catch {
    return null
  }
  // Newer ICU accepts a bare UTC offset ('+05:30', and '−04:00' with U+2212
  // MINUS SIGN, which it resolves to '-04:00') as a zone; the API rejects it
  // (PostgreSQL reads it with the opposite sign), so the web does too. Every
  // IANA name starts with a letter, so the RESOLVED id is checked.
  if (!/^[A-Za-z]/.test(resolved)) return null
  return LEGACY_TIME_ZONE_ALIASES[resolved] ?? resolved
}

/** The browser's zone as a current IANA id (never 'Asia/Calcutta'); UTC if unreadable. */
export function browserTimeZone(): string {
  try {
    return normalizeTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone) ?? 'UTC'
  } catch {
    return 'UTC'
  }
}
